import React, { useRef, useEffect, useState } from "react";
import {
    FilterProps,
    FilterValues,
    KmsTypeFilterDropdown,
    KmsTypeFilterItemDropdown,
} from "@kaltura/mediaspace-shared-types";
import { getDisabledFilters } from "../../vertical-filter-bar/dependencyUtils";
import styled from "@emotion/styled";
import { MenuItem } from "@kaltura/ds-react-components";
import { Check16Icon } from "@kaltura/ds-react-icons";

const StyledWrapper = styled("div")(({ theme }) => ({
    padding: theme.spacing(0, 0),
}));

const StyledIcon = styled(Check16Icon)(({ theme }) => ({
    fontSize: "1em",
}));

/**
 * responsible for disabling relevant items per current
 * selections (values) <s>and hooking-up the radio filter</s>
 */
export function FieldsSelector(props: FilterProps<KmsTypeFilterDropdown>) {
    const { param, values, defaultValue, onChange } = props;

    let items = props.items;
    let currentSelection = defaultValue;
    if (values) {
        // disable filter items per current selected values
        items = getDisabledFilters(values, [props])[0].items;
        if (values[param]) {
            // this filter is single selection, the value will always be a string, not string[].
            currentSelection = values[param].toString();
        }
    }

    const itemRefs = useRef<(HTMLElement | null)[]>([]);
    const [focusedIndex, setFocusedIndex] = useState<number>(() => {
        const selectedIndex = items.findIndex((item) => item.value === currentSelection);
        return selectedIndex >= 0 ? selectedIndex : 0;
    });

    // Focus on initial render
    useEffect(() => {
        itemRefs.current[focusedIndex]?.focus();
    }, [focusedIndex]);

    const handleChange = (item: KmsTypeFilterItemDropdown) => {
        const filterValues: FilterValues = { [param]: item.value };
        onChange(filterValues);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            const next = (focusedIndex + 1) % items.length;
            setFocusedIndex(next);
        }
        else if (e.key === "ArrowUp") {
            e.preventDefault();
            const prev = (focusedIndex - 1 + items.length) % items.length;
            setFocusedIndex(prev);
        }
        else if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            const item = items[focusedIndex];
            if (!item.disabled) {
                handleChange(item);
            }
        }
    };

    return (
        <StyledWrapper
            role={"listbox"}
            aria-labelledby={"search-in-fields-button"}
            tabIndex={0}
            onKeyDown={handleKeyDown}
        >
            {items.map((item, index) => (
                <MenuItem
                    key={item.value + index}
                    ref={(el) => (itemRefs.current[index] = el)}
                    tabIndex={index === focusedIndex ? 0 : -1}
                    aria-selected={item.disabled ? undefined : item.value === currentSelection}
                    value={item.value}
                    onClick={() => handleChange(item)}
                    disabled={item.disabled}
                    icon={item.value === currentSelection ? <StyledIcon /> : undefined}
                    role="option"
                >
                    {item.label}
                </MenuItem>
            ))}
        </StyledWrapper>
    );
}

export default FieldsSelector;
